import React, { useMemo, useState } from 'react';
import './Sidebar.css';
import companiesData from '../data/companies.json';
import { useGlobalData } from '../context/GlobalDataContext';

const Sidebar = ({ cryptoType, activeView, onViewChange }) => {
  const { latestSnapshotData } = useGlobalData();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.sidebar-dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);
  
  const companies = (companiesData[cryptoType] || []).filter(company => company.trackData === true);
  
  const getCryptoColor = () => {
    switch (cryptoType) {
      case 'bitcoin':
        return '#f7931a';
      case 'ethereum':
        return '#627eea';
      case 'solana':
        return '#9945ff';
      default:
        return '#00ffff';
    }
  };
  
  // Create a map of ticker to latest data
  const tickerDataMap = useMemo(() => {
    if (!latestSnapshotData) return {};
    const map = {};
    latestSnapshotData.forEach(row => {
      map[row.ticker] = row;
    });
    return map;
  }, [latestSnapshotData]);
  
  // Sort companies by total holdings (crypto_per_share * outstanding_shares_estimated)
  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      // For Solana only, always put "Solana Company" at the top
      if (cryptoType === 'solana') {
        if (a.ticker === 'HSDT' && b.ticker !== 'HSDT') return -1;
        if (b.ticker === 'HSDT' && a.ticker !== 'HSDT') return 1;
      }
      
      const aData = tickerDataMap[a.ticker];
      const bData = tickerDataMap[b.ticker];
      
      // If either company doesn't have data, put it at the end
      if (!aData && !bData) return 0;
      if (!aData) return 1;
      if (!bData) return -1;
      
      const aValue = (aData.crypto_per_share || 0) * (aData.outstanding_shares_estimated || 0);
      const bValue = (bData.crypto_per_share || 0) * (bData.outstanding_shares_estimated || 0);
      
      // Sort descending (highest first)
      return bValue - aValue;
    });
  }, [companies, tickerDataMap, cryptoType]);

  // Get current selection label
  const getCurrentLabel = () => {
    if (activeView === 'overview') return 'Asset Overview';
    const company = sortedCompanies.find(c => c.id === activeView);
    return company ? `${company.ticker} - ${company.name}` : 'Select Company';
  };

  // Mobile dropdown view
  if (isMobile) {
    return (
      <div className="sidebar sidebar-mobile" style={{ '--crypto-color': getCryptoColor() }}>
        <div className="sidebar-dropdown-container">
          <button
            className="sidebar-dropdown-toggle"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{ '--crypto-color': getCryptoColor() }}
          >
            <span>{getCurrentLabel()}</span>
            <svg 
              className={`sidebar-dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {isDropdownOpen && (
            <div className="sidebar-dropdown-menu">
              <button
                className={`sidebar-dropdown-item ${activeView === 'overview' ? 'active' : ''}`}
                onClick={() => {
                  onViewChange('overview');
                  setIsDropdownOpen(false);
                }}
              >
                Asset Overview
              </button>
              {sortedCompanies.map((company) => (
                <button
                  key={company.id}
                  className={`sidebar-dropdown-item ${activeView === company.id ? 'active' : ''}`}
                  onClick={() => {
                    onViewChange(company.id);
                    setIsDropdownOpen(false);
                  }}
                >
                  {company.ticker} - {company.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop sidebar view
  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <button 
        className="sidebar-collapse-btn"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{ '--crypto-color': getCryptoColor() }}
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <nav className="sidebar-nav">
        <button
          className={`sidebar-item overview-item ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => onViewChange('overview')}
          style={{
            '--crypto-color': getCryptoColor(),
            borderLeftColor: activeView === 'overview' ? getCryptoColor() : 'transparent'
          }}
        >
          <div className="company-info">
            <span className="company-name">Asset Overview</span>
          </div>
        </button>

        <div className="sidebar-section-title">Companies</div>
        <div className="sidebar-divider"></div>
        
        <div className="sidebar-companies">
          {sortedCompanies.map((company) => {
            return (
              <button
                key={company.id}
                className={`sidebar-item company-item ${activeView === company.id ? 'active' : ''}`}
                onClick={() => onViewChange(company.id)}
                style={{
                  '--crypto-color': getCryptoColor(),
                  borderLeftColor: activeView === company.id ? getCryptoColor() : 'transparent'
                }}
                title={company.name}
              >
                <div className="company-info">
                  <span className="company-name">{company.name}</span>
                  <span className="company-ticker-small">{company.ticker}</span>
                </div>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;

